import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PAGE_CONFIG } from './Config';
import EmptyPage from '../../components/EmptyPage';
import { useTranslation } from 'react-i18next';
import PageIntro from '../../components/PageIntro';
import ContentCard from '../../components/ContentCard';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';

import { isRegionalCourtsType } from '../../catalog/catalogGroups';

const RegionalCases = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`regionalCases:${key}`), [t]);
    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [materialTypeId, setMaterialTypeId] = useState(null);
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    useEffect(() => {
        if (materialTypes && materialTypes.length > 0) {
            const materialType = materialTypes.find((i) => isRegionalCourtsType(i.name));
            if (materialType) {
                setMaterialTypeId(materialType.id);
            } else {
                console.warn(
                    'No Regional Courts Cases material type found. Available types:',
                    materialTypes.map((mt) => mt.name)
                );
            }
        }
    }, [materialTypes]);

    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: materialTypeId
        }),
        [params, materialTypeId]
    );

    const { data: casesResponse, isLoading } = useQuery({
        queryKey: ['regionalCases', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!materialTypeId,
        select: (data) => data?.data
    });

    const cases = casesResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: casesResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    return (
        <div>
            <PageIntro description={pageConfig.PAGE_DESCRIPTION} />
            <div className="text-sm text-gray-500 ml-2 mt-4 dark:text-gray-400">
                {tr('showing')} {cases?.length || 0} {tr('of')} {pagination.totalCount}
            </div>

            {cases && cases.length > 0 ? (
                <ContentCard data={cases} />
            ) : (
                <EmptyPage
                    colSpan={12}
                    isLoading={isLoading || !materialTypeId}
                    icon={pageConfig.EMPTY_PAGE_ICON}
                    description={pageConfig.SEARCH_DESCRIPTION}
                    placeholder={pageConfig.SEARCH_PLACEHOLDER}
                    loadingMessage={pageConfig.SEARCH_LOADING_MESSAGE}
                />
            )}

            {cases && cases.length > 0 && (
                <TablePagination
                    totalPages={totalPages}
                    page={pagination.page}
                    limit={pagination.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    tableStyles={tableStyles}
                />
            )}
        </div>
    );
};

export default RegionalCases;
